import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiUsageApi,
  type AiUsageQuery,
  type AiUsageResponse,
} from '@/modules/platform/services/aiUsageApi';

/**
 * Phase 39.3 — Platform AI usage hook (D-18).
 *
 * Uses `refetchInterval` — the canonical polling pattern in this codebase
 * (research §Q6; existing usages: weather, AI job status, WP sync). No
 * toasts on success or failure (memory rule `feedback_silent_background_sync.md`
 * — background auto-refreshes must be invisible).
 *
 * Do NOT add `select: (r) => r.data` — `aiUsageApi.get` delegates to
 * `api.getOne` which already unwraps the `{data:...}` envelope (memory rule
 * `feedback_api_envelope_unwrap.md`).
 *
 * `dataUpdatedAt` from the query result is the source of truth for the
 * "Last updated Xm ago" freshness indicator in `UsageTab` — no separate
 * timestamp state needed.
 */
export function usePlatformAIUsage(
  query: AiUsageQuery = {},
  options?: { enabled?: boolean },
) {
  return useQuery<AiUsageResponse>({
    queryKey: ['platform', 'ai', 'usage', query],
    queryFn: () => aiUsageApi.get(query),
    staleTime: 5 * 60 * 1000, // 5 min — matches refresh cadence (D-18)
    refetchInterval: 5 * 60 * 1000, // 5-min silent auto-refresh (D-18)
    retry: 1,
    enabled: options?.enabled ?? true,
  });
}

/**
 * #647 — manual, user-initiated OR-balance refresh (the counterpart of the
 * silent 5-min poll above). Busts the server cache, then invalidates every
 * usage query so widgets refetch; the refetch is served from the cache the
 * refresh call just re-populated, so OpenRouter is hit exactly once.
 */
export function useRefreshPlatformAIUsage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => aiUsageApi.refresh(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'ai', 'usage'] });
    },
  });
}
